import type { DocumentType } from '@/store/dataCatalogSlice'

export const PNID_PFD_FILE_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'tiff']
export const DOCUMENT_FILE_EXTENSIONS = ['pdf', 'docx', 'pptx', 'txt', 'csv', 'xlsx']
export const TIME_SERIES_FILE_EXTENSIONS = ['csv', 'xlsx']

// Maximum file size in MB (must match backend setting)
export const MAX_FILE_SIZE_MB = 512

/**
 * Check if a file exceeds the maximum upload size
 */
export const isFileTooLarge = (file: File): boolean => {
  const maxSizeBytes = MAX_FILE_SIZE_MB * 1024 * 1024
  return file.size > maxSizeBytes
}

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

// PLC program exports (VISN-305). Vendor extensions are unambiguous; xml is
// generic and must be dropdown-selected as a PLC program.
export const PLC_FILE_EXTENSIONS = ['xml', 'tcpou', 'st', 'scl', 'l5x']

// Combine all supported extensions into a single array (with duplicates removed)
export const ALL_SUPPORTED_EXTENSIONS = [
  ...PNID_PFD_FILE_EXTENSIONS,
  ...DOCUMENT_FILE_EXTENSIONS,
  ...TIME_SERIES_FILE_EXTENSIONS,
  ...PLC_FILE_EXTENSIONS,
  'json', 'dwg', 'dxf',
].filter((v, i, a) => a.indexOf(v) === i)

/** Pre-built accept string for file inputs that accept all supported types. */
export const ALL_SUPPORTED_ACCEPT_STRING = ALL_SUPPORTED_EXTENSIONS.map(ext => `.${ext}`).join(',')

// Check if a file extension is supported
export const isFileTypeSupported = (fileName: string): boolean => {
  const extension = fileName.split('.').pop()?.toLowerCase() || ''
  return ALL_SUPPORTED_EXTENSIONS.includes(extension)
}

/**
 * Get accepted file extensions for a document type
 * Returns array of extensions without dots (e.g., ['csv', 'xlsx'])
 */
export const getAcceptedFileTypes = (type: DocumentType): string[] => {
  switch (type) {
    case 'pnid':
    case 'pfd':
      return PNID_PFD_FILE_EXTENSIONS
    case 'document':
      return DOCUMENT_FILE_EXTENSIONS
    case 'timeseries':
      return TIME_SERIES_FILE_EXTENSIONS
    default:
      return []
  }
}

/**
 * Convert extension array to HTML accept attribute format
 * e.g., ['json', 'csv'] => '.json,.csv'
 */
export const extensionsToAcceptString = (extensions: string[]): string => {
  return extensions.map(ext => `.${ext}`).join(',')
}
